import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Search,
  Eye,
  ArrowLeft,
  X,
  Download,
  Mail,
  Send,
  AlertCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { payslipsApi } from '../../../api/payslipsApi';
import { toast } from 'react-hot-toast';
import { formatDate } from '../../../utils/stringUtils';
import { generatePayslipPDF } from '../../../utils/generatePayslipPDF';
import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import ExportModal from '../../../components/common/ExportModal';

const formatPeriodString = (periodString) => {
  if (!periodString) return 'N/A';

  // Parse period string like "01/11/2025 - 30/11/2025"
  const parts = periodString.split(' - ');
  if (parts.length !== 2) return periodString; // Return original if format is unexpected

  try {
    // Parse DD/MM/YYYY format
    const parseDate = (dateStr) => {
      const [day, month, year] = dateStr.trim().split('/');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    };

    const startDate = parseDate(parts[0]);
    const endDate = parseDate(parts[1]);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return periodString; // Return original if parsing fails
    }

    const startFormatted = formatDate(startDate, { includeTime: false });
    const endFormatted = formatDate(endDate, { includeTime: false });

    return `${startFormatted} - ${endFormatted}`;
  } catch (error) {
    return periodString; // Return original if any error occurs
  }
};

const PayslipBatchDetails = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [payslipPage, setPayslipPage] = useState(1);
  const [payslipSearch, setPayslipSearch] = useState('');
  const [debouncedPayslipSearch, setDebouncedPayslipSearch] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const payslipPageSize = 20;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedPayslipSearch(payslipSearch.trim());
      setPayslipPage(1); // Reset to first page on search
    }, 400);
    return () => clearTimeout(handler);
  }, [payslipSearch]);

  // Fetch payslip details for the batch
  const {
    data: payslipDetailsData,
    isLoading: isDetailsLoading,
    refetch: refetchDetails
  } = useQuery({
    queryKey: ['payslipDetails', batchId, payslipPage, debouncedPayslipSearch],
    queryFn: async () => {
      if (!batchId) return { payslips: [], batch: null, pagination: null };
      // Use PayslipBatch details endpoint with pagination and search
      const response = await payslipsApi.getPayslipBatchDetails(
        batchId,
        payslipPage,
        payslipPageSize,
        debouncedPayslipSearch
      );

      // Extract data from response
      let payslips = [];
      let pagination = null;
      let batch = null;

      if (response && typeof response === 'object') {
        if (response.responseCode === 200 && response.data) {
          // Standard structure: { responseCode, responseMessage, data: { payslips, pagination, batch } }
          payslips = response.data.payslips || [];
          pagination = response.data.pagination || null;
          batch = response.data.batch || null;
        } else if (Array.isArray(response.data)) {
          // Fallback: response.data is array
          payslips = response.data;
        }
      }

      return {
        payslips: payslips,
        pagination: pagination || {
          page: payslipPage,
          limit: payslipPageSize,
          totalCount: payslips.length,
          totalPages: Math.ceil(payslips.length / payslipPageSize),
          hasNextPage: false,
          hasPrevPage: payslipPage > 1
        },
        batch: batch
      };
    },
    enabled: !!batchId,
    staleTime: 0,
    onError: (error) => {
      toast.error('Error loading payslip details');
    }
  });

  const payslipDetails = payslipDetailsData?.payslips || [];
  const batchInfo = payslipDetailsData?.batch || null;
  const payslipPagination = payslipDetailsData?.pagination || null;

  // Client-side filtering (search is handled by backend, but status filter is client-side)
  const filteredPayslipDetails = React.useMemo(() => {
    let filtered = payslipDetails;

    // Apply status filter (search is handled by backend)
    if (filterStatus !== 'all') {
      filtered = filtered.filter(payslip => payslip.status === filterStatus);
    }

    return filtered;
  }, [payslipDetails, filterStatus]);

  // Use backend pagination data, but apply client-side status filter
  const paginatedPayslips = filteredPayslipDetails;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedPayslips = React.useMemo(() => {
    if (!paginatedPayslips) return [];
    if (!sortField) return paginatedPayslips;

    return [...paginatedPayslips].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'employeeName':
          aValue = (a.employeeName || '').toLowerCase();
          bValue = (b.employeeName || '').toLowerCase();
          break;
        case 'employeeId':
          aValue = (a.employeeId || '').toLowerCase();
          bValue = (b.employeeId || '').toLowerCase();
          break;
        case 'department':
          aValue = (a.department || '').toLowerCase();
          bValue = (b.department || '').toLowerCase();
          break;
        case 'netPay':
          aValue = Number(a.netPay || 0);
          bValue = Number(b.netPay || 0);
          break;
        case 'presentDays':
          aValue = Number(a.presentDays !== undefined ? a.presentDays : (a.workingDays || 0));
          bValue = Number(b.presentDays !== undefined ? b.presentDays : (b.workingDays || 0));
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'totalMonthPay':
          aValue = Number(a.totalMonthPay || (a.paidPreviously + (a.paidAmount || a.finalPay)) || 0);
          bValue = Number(b.totalMonthPay || (b.paidPreviously + (b.paidAmount || b.finalPay)) || 0);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [paginatedPayslips, sortField, sortOrder]);

  // Reset selection when filters change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [debouncedPayslipSearch, filterStatus, payslipPage]);

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = sortedPayslips.map(p => p.id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      const selectedData = sortedPayslips.filter(p => selectedRows.has(p.id));
      const exportData = selectedData.map(p => ({
        'Employee Name': p.employeeName || 'N/A',
        'Employee ID': p.employeeId || 'N/A',
        'Department': p.department || 'N/A',
        'Email': p.email || 'N/A',
        'Net Pay': p.netPay || 0,
        'Present Days': p.presentDays !== undefined ? p.presentDays : p.workingDays || 0,
        'Status': p.status || 'N/A'
      }));

      exportToCSV(exportData, 'payslips_selected', {
        title: `Payslips - ${batchInfo?.payGroupName || 'N/A'}`,
        type: 'Selected Records'
      });
      toast.success('Selected records exported successfully');
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportCurrentPage = () => {
    if (sortedPayslips.length === 0) {
      toast.error('No data to export');
      return;
    }

    const exportData = sortedPayslips.map(p => ({
      'Employee Name': p.employeeName || 'N/A',
      'Employee ID': p.employeeId || 'N/A',
      'Department': p.department || 'N/A',
      'Email': p.email || 'N/A',
      'Net Pay': p.netPay || 0,
      'Present Days': p.presentDays !== undefined ? p.presentDays : p.workingDays || 0,
      'Status': p.status || 'N/A'
    }));

    exportToCSV(exportData, 'payslips_current_page', {
      title: `Payslips - ${batchInfo?.payGroupName || 'N/A'}`,
      type: 'Current Page'
    });
    setShowExportModal(false);
    toast.success('Current page exported successfully');
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("mode", "payslips");
      queryParams.append("type", `Date Range (${startDate} to ${endDate})`);
      queryParams.append("batchIds", batchId);
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);
      if (debouncedPayslipSearch) queryParams.append("search", debouncedPayslipSearch);
      if (filterStatus !== 'all') queryParams.append("status", filterStatus);

      const url = `${process.env.REACT_APP_API_BASE_URL}/payroll/payslips/export?${queryParams.toString()}`;
      const filename = generateExportFilename('payslips_range');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      toast.success('Export started successfully');
    } catch (error) {
      toast.error("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("mode", "payslips");
      queryParams.append("type", "All Data in Batch");
      queryParams.append("batchIds", batchId);
      if (debouncedPayslipSearch) queryParams.append("search", debouncedPayslipSearch);
      if (filterStatus !== 'all') queryParams.append("status", filterStatus);

      const url = `${process.env.REACT_APP_API_BASE_URL}/payroll/payslips/export?${queryParams.toString()}`;
      const filename = generateExportFilename('all_batch_payslips');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      toast.success('All data export started');
    } catch (error) {
      toast.error("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'withheld': return 'bg-red-100 text-red-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewPayslip = (payslip) => {
    navigate(`/payroll/payslips/${payslip.id || payslip._id}`);
  };

  const sendPayslipEmailMutation = useMutation({
    mutationFn: async (payslipId) => {
      const response = await payslipsApi.sendPayslipEmail(payslipId);
      if (response && response.responseCode && response.responseCode !== 200) {
        throw new Error(response.responseMessage || 'Failed to send payslip email');
      }
      return response;
    },
    onSuccess: (data) => {
      const pdfAttached = data?.data?.pdfAttached ?? data?.pdfAttached;
      const message = pdfAttached
        ? 'Payslip email sent with PDF attachment!'
        : (data?.responseMessage || data?.message || 'Payslip email sent successfully');
      toast.success(message);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.responseMessage
        || error?.response?.data?.message
        || error?.message
        || 'Failed to send payslip email. Please try again.';
      toast.error(errorMessage);
    }
  });

  const handleDownloadPayslip = async (payslipId) => {
    if (!payslipId) return;

    try {
      // Find the payslip from the list
      const payslip = payslipDetails.find(p => p.id === payslipId || p._id === payslipId);

      if (!payslip) {
        toast.error('Payslip not found');
        return;
      }

      // Ensure jspdf-autotable is loaded
      try {
        await import('jspdf-autotable');
      } catch (importError) {
      }

      // Generate PDF on client side
      const doc = await generatePayslipPDF(payslip);

      // Generate filename with download timestamp
      const now = new Date();
      const hours = now.getHours();
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const hours12 = String(hours % 12 || 12).padStart(2, '0');
      const timeStamp = `${hours12}-${minutes}${ampm}`;
      const fileName = `Payslip_${payslip.employeeName?.replace(/\s+/g, '_') || 'Employee'}_${payslip.employeeId || payslipId}_${timeStamp}.pdf`;

      // Save the PDF
      doc.save(fileName);

      toast.success('Payslip downloaded successfully!');
    } catch (error) {
      toast.error(`Failed to generate payslip PDF: ${error.message || 'Unknown error occurred. Please try again later.'}`);
    }
  };

  // Confirmation state for single payslip email send
  // Shape: { payslipId, employeeName, employeeId } | null
  const [confirmSendPayslip, setConfirmSendPayslip] = useState(null);

  const handleSendEmail = (payslip) => {
    if (!payslip?.id) return;
    setConfirmSendPayslip({
      payslipId: payslip.id,
      employeeName: payslip.employeeName || payslip.name || 'this employee',
      employeeId: payslip.employeeId || ''
    });
  };

  const handleConfirmSendEmail = () => {
    if (!confirmSendPayslip?.payslipId) return;
    const { payslipId } = confirmSendPayslip;
    setConfirmSendPayslip(null);
    // Backend generates the PDF server-side using generatePayslipPDFNode
    sendPayslipEmailMutation.mutate(payslipId);
  };



  return (
    <div className="space-y-6">
      {/* Header with back button, title, and search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                icon={<ArrowLeft className="h-4 w-4" />}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
              {batchInfo && (
                <div className="flex flex-col">
                  <h1 className="text-2xl font-bold text-gray-900 leading-tight">
                    {batchInfo.payGroupName} - {batchInfo.payrollMonth}
                  </h1>
                  {(batchInfo.generatedDate || batchInfo.createdAt) && (
                    <p className="text-xs font-medium text-gray-500 mt-1 flex items-center">
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2"></span>
                      Generated on: {formatDate(batchInfo.generatedDate || batchInfo.createdAt, { includeTime: true })}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center space-x-4">
              <PermissionGate permission="payroll" action="read">
                <Button
                  variant="outline"
                  icon={<Download className={`h-4 w-4 ${exportLoading ? 'animate-spin' : ''}`} />}
                  disabled={exportLoading}
                  onClick={handleExportClick}
                >
                  {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}
                </Button>
              </PermissionGate>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={payslipSearch}
                  onChange={(e) => setPayslipSearch(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-0 focus:ring-0 focus:border-brand"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payslip Details Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-transparent" style={{ backgroundColor: "rgba(59, 130, 246, 0.14)" }}>
              <tr>
                <th className="px-6 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-brand focus:ring-0"
                    checked={sortedPayslips.length > 0 && selectedRows.size === sortedPayslips.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('employeeName')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Employee</span>
                    {sortField === 'employeeName' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('employeeId')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Employee ID</span>
                    {sortField === 'employeeId' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('department')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Department</span>
                    {sortField === 'department' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('mode')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Mode</span>
                    {sortField === 'mode' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('input')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Input</span>
                    {sortField === 'input' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('netPay')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Total Net Pay</span>
                    {sortField === 'netPay' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('paidAmount')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Paid</span>
                    {sortField === 'paidAmount' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('totalMonthPay')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Total Paid</span>
                    {sortField === 'totalMonthPay' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('holdAmount')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Remaining/Hold</span>
                    {sortField === 'holdAmount' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('presentDays')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Present Days</span>
                    {sortField === 'presentDays' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {sortField === 'status' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isDetailsLoading ? (
                <tr>
                  <td colSpan="12" className="px-6 py-8 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand"></div>
                      <span className="ml-2 text-gray-500">Loading payslip details...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedPayslips.length === 0 ? (
                <tr>
                  <td colSpan="12" className="px-6 py-8 text-center text-gray-500">
                    {filteredPayslipDetails.length === 0 ? 'No payslips found' : 'No payslips on this page'}
                  </td>
                </tr>
              ) : (
                sortedPayslips.map((payslip) => (
                  <tr key={payslip.id} className="hover:bg-blue-50/30">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-brand focus:ring-0"
                        checked={selectedRows.has(payslip.id)}
                        onChange={() => handleSelectRow(payslip.id)}
                      />
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <div className="text-sm font-medium text-gray-900 truncate" title={payslip.employeeName || 'N/A'}>
                        {payslip.employeeName || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500 truncate" title={payslip.email || 'N/A'}>
                        {payslip.email || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 max-w-[120px]">
                      <div className="text-sm text-gray-900 truncate" title={payslip.employeeId || 'N/A'}>
                        {payslip.employeeId || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 max-w-[200px]">
                      <div className="text-sm text-gray-900 truncate" title={payslip.department || 'N/A'}>
                        {payslip.department || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-md ${payslip.mode === 'Percentage' ? 'bg-purple-100 text-purple-700' :
                        payslip.mode === 'Fixed' ? 'bg-orange-100 text-orange-700' :
                          'bg-blue-100 text-[#0B2033]'
                        }`}>
                        {payslip.mode || 'Full Salary'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {payslip.mode === 'Percentage' ? `${payslip.input || 0}%` :
                        payslip.mode === 'Fixed' ? `₹${(payslip.input || 0).toLocaleString('en-IN')}` :
                          '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ₹{(payslip.netPay || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      ₹{(payslip.paidAmount || payslip.finalPay || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-800">
                      ₹{(payslip.totalMonthPay || (payslip.paidPreviously + (payslip.paidAmount || payslip.finalPay || 0))).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-orange-600">
                      ₹{(payslip.holdAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {typeof payslip.presentDays === 'number' ? payslip.presentDays : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusColor(payslip.status)}`}>
                        {payslip.status || 'draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <PermissionGate permission="payroll" action="read">
                          <button
                            onClick={() => handleViewPayslip(payslip)}
                            className="text-[#0F2942] hover:text-blue-900"
                            title="View Payslip"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="payroll" action="read">
                          <button
                            onClick={() => handleDownloadPayslip(payslip.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Download Payslip"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="payroll" action="create">
                          <button
                            onClick={() => handleSendEmail(payslip)}
                            disabled={sendPayslipEmailMutation.isPending}
                            className="text-purple-600 hover:text-purple-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Send Payslip Email"
                          >
                            <Mail className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {payslipPagination && payslipPagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing page {payslipPagination.page} of {payslipPagination.totalPages}
              ({payslipPagination.totalCount} total payslips)
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  setPayslipPage(p => Math.max(1, p - 1));
                }}
                disabled={!payslipPagination.hasPrevPage}
                className={`px-4 py-2 border rounded-lg ${payslipPagination.hasPrevPage
                  ? 'border-gray-300 hover:bg-blue-50/30 text-gray-700'
                  : 'border-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
              >
                Previous
              </button>
              <button
                onClick={() => {
                  setPayslipPage(p => Math.min(payslipPagination.totalPages, p + 1));
                }}
                disabled={!payslipPagination.hasNextPage}
                className={`px-4 py-2 border rounded-lg ${payslipPagination.hasNextPage
                  ? 'border-gray-300 hover:bg-blue-50/30 text-gray-700'
                  : 'border-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Payslip Email Confirmation Dialog ────────────────────────────── */}
      {confirmSendPayslip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm !mt-0">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-100">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Mail className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Send Payslip Email</h3>
                <p className="text-xs text-gray-500 mt-0.5">A payslip PDF will be generated and attached</p>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <p className="text-sm text-gray-700">
                You are about to send a payslip to{' '}
                <strong className="text-purple-700">{confirmSendPayslip.employeeName}</strong>
                {confirmSendPayslip.employeeId && (
                  <span className="text-gray-500"> ({confirmSendPayslip.employeeId})</span>
                )}.
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button
                onClick={() => setConfirmSendPayslip(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-blue-50/30 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSendEmail}
                className="px-5 py-2 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
              >
                <Send className="h-4 w-4" />
                Yes, Send Email
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExportCurrentPage={handleExportCurrentPage}
          onExportRange={handleExportRange}
          onExportAll={handleExportAll}
          loading={exportLoading}
        />
      )}
    </div>
  );
};

export default PayslipBatchDetails;

